/**
 * Releases and the rollout (D15, the vendor's support tool — never a
 * customer control): the latest release, the recently published ones, the
 * stage pin and the site pins (set and clear for a `vendor.admin`), and per
 * node the release it is served (site pin → stage pin → latest) against the
 * one it holds, with "n of m nodes on target". A site pin is a tenant's; a
 * legacy one (keyed by site id alone) is marked, re-pinned per tenant, and
 * cleared through a tenant with no pin of its own for that site
 * (`legacyClearTenant`).
 */
import type { ReleasePinWire } from '@acp/api-contract/releases.js';
import type { VendorReleasesResponseWire } from '@acp/api-contract/vendor.js';
import { PinIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import { legacyClearTenant, linkTone, rolloutSummary, shortHash } from '../../lib/vendor.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type ReleasesPanelProps = {
  readonly releases: VendorReleasesResponseWire | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly canAdmin: boolean;
  readonly onRetry: () => void;
  /** Open the pin dialog: the stage, or a tenant's site ('' asks for that id). */
  readonly onPinStage: () => void;
  readonly onPinSite: (tenantId: string, siteId: string) => void;
  readonly onClearStage: () => void;
  /** Clear a tenant's site pin (which also removes a legacy pin of that site id). */
  readonly onClearSite: (tenantId: string, siteId: string) => void;
};

function PinText({ pin }: { readonly pin: ReleasePinWire }) {
  return (
    <span className="text-sm">
      <span className="font-mono">{shortHash(pin.listHash)}</span>
      {pin.note === '' ? '' : ` — ${pin.note}`}
      <span className="text-muted-foreground"> · since {pin.pinnedAt.slice(0, 10)}</span>
    </span>
  );
}

export function ReleasesPanel({ releases, loading, error, canAdmin, onRetry, onPinStage, onPinSite, onClearStage, onClearSite }: ReleasesPanelProps) {
  return (
    <div className="grid gap-6" data-testid="releases-panel">
      {error === null ? null : (
        <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
          <span className="text-destructive">Could not load releases: {error}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCwIcon />
            Retry
          </Button>
        </div>
      )}
      {releases === null && loading ? <p className="py-8 text-center text-sm text-muted-foreground">Loading releases…</p> : null}
      {releases === null ? null : <Loaded releases={releases} canAdmin={canAdmin} onPinStage={onPinStage} onPinSite={onPinSite} onClearStage={onClearStage} onClearSite={onClearSite} />}
    </div>
  );
}

function Loaded({ releases, canAdmin, onPinStage, onPinSite, onClearStage, onClearSite }: Omit<ReleasesPanelProps, 'releases' | 'loading' | 'error' | 'onRetry'> & { readonly releases: VendorReleasesResponseWire }) {
  const summary = rolloutSummary(releases.rollout);
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Pins</CardTitle>
          <CardDescription>Precedence: site pin → stage pin → latest ({releases.latest === null ? 'nothing published' : <span className="font-mono">{shortHash(releases.latest)}</span>}).</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3">
          <div className="flex flex-wrap items-center gap-2" data-testid="stage-pin">
            <span className="w-24 text-sm font-medium">Stage</span>
            {releases.stagePin === null ? <span className="text-sm text-muted-foreground">not pinned — serves the latest</span> : <PinText pin={releases.stagePin} />}
            {canAdmin ? (
              <span className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={onPinStage} disabled={releases.releases.length === 0} data-testid="pin-stage">
                  <PinIcon />
                  {releases.stagePin === null ? 'Pin' : 'Change'}
                </Button>
                {releases.stagePin === null ? null : (
                  <Button size="sm" variant="outline" onClick={onClearStage} data-testid="clear-stage-pin">
                    <XIcon />
                    Clear
                  </Button>
                )}
              </span>
            ) : null}
          </div>
          {releases.sitePins.map((p) => {
            const clearVia = p.tenantId ?? legacyClearTenant(releases, p.siteId);
            return (
              <div key={`${p.tenantId ?? ''}/${p.siteId}`} className="flex flex-wrap items-center gap-2" data-testid="site-pin" data-tenant={p.tenantId ?? ''} data-site={p.siteId}>
                <span className="w-48 font-mono text-sm">{p.tenantId === null ? p.siteId : `${p.tenantId}/${p.siteId}`}</span>
                {p.legacy ? (
                  <Badge variant="warning" title="Keyed by site id alone: holds every tenant's site of this id without a pin of its own. Re-pin per tenant." data-testid="legacy-pin">
                    legacy
                  </Badge>
                ) : null}
                <PinText pin={p.pin} />
                {canAdmin ? (
                  <span className="ml-auto flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onPinSite(p.tenantId ?? '', p.siteId)} data-testid="change-site-pin">
                      <PinIcon />
                      {p.legacy ? 'Re-pin' : 'Change'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => (clearVia === null ? undefined : onClearSite(clearVia, p.siteId))} disabled={clearVia === null} data-testid="clear-site-pin">
                      <XIcon />
                      Clear
                    </Button>
                  </span>
                ) : null}
              </div>
            );
          })}
          {canAdmin ? (
            <Button size="sm" variant="outline" className="w-fit" onClick={() => onPinSite('', '')} disabled={releases.releases.length === 0} data-testid="pin-site">
              <PinIcon />
              Pin a site
            </Button>
          ) : null}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Rollout</CardTitle>
          <CardDescription data-testid="rollout-summary">
            {summary.onTarget} of {summary.total} nodes on target
          </CardDescription>
        </CardHeader>
        <CardContent>
          {releases.rollout.length === 0 ? (
            <p className="text-sm text-muted-foreground">No nodes enrolled.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Node</TableHead>
                  <TableHead>Link</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Holds</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {releases.rollout.map((n) => (
                  <TableRow key={`${n.tenantId}/${n.nodeId}`} data-testid="rollout-row" data-node={n.nodeId}>
                    <TableCell className="font-mono text-xs">{n.tenantId}</TableCell>
                    <TableCell className="text-sm">{n.siteId}</TableCell>
                    <TableCell className="text-sm">
                      {n.label} <span className="text-muted-foreground">({n.role})</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={linkTone(n.linkState)}>{n.linkState}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="font-mono">{shortHash(n.target.listHash)}</span> <span className="text-muted-foreground">({n.target.source})</span>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{shortHash(n.running)}</TableCell>
                    <TableCell>
                      <Badge variant={n.upToDate ? 'success' : 'warning'} data-testid="rollout-state">
                        {n.upToDate ? 'on target' : 'pending'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Published releases</CardTitle>
          <CardDescription>The most recent, newest first.</CardDescription>
        </CardHeader>
        <CardContent>
          {releases.releases.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing published yet (`just release-publish`).</p>
          ) : (
            <Table data-testid="release-list">
              <TableHeader>
                <TableRow>
                  <TableHead>List</TableHead>
                  <TableHead>Commit</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>Packages</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {releases.releases.map((r) => (
                  <TableRow key={r.listHash} data-testid="release-row">
                    <TableCell className="font-mono text-sm">
                      {shortHash(r.listHash)}
                      {r.listHash === releases.latest ? (
                        <Badge variant="success" className="ml-2">
                          latest
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{r.commit}</TableCell>
                    <TableCell className="text-sm">{r.publishedAt === null ? '–' : r.publishedAt.slice(0, 16).replace('T', ' ')}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.packages.map((p) => `${p.name} ${shortHash(p.hash).slice(0, 8)}`).join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
